import { supabase } from "@/integrations/supabase/client";
import type { OCRResult } from "@/components/CameraScanner";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { onlyActiveCompany } from "@/lib/activeCompanyLookup";

export type ApplianceMatch = {
  id: string;
  name: string | null;
  brand: string | null;
  model_number: string | null;
  serial_number: string | null;
  product_number: string | null;
  company_id?: string | null;
};

type WorkOrderMatch = {
  id: string;
  type: string;
  status: string | null;
  appliance_id: string;
  updated_at: string | null;
  created_at: string | null;
};

export type ScanDestination = {
  path: string;
  kind: "work-order" | "appliance";
  applianceId: string;
  workOrderId?: string;
};

const normalize = (value?: string | null) =>
  (value || "").toLowerCase().replace(/[\s\-\/_.]/g, "");

const escapeLike = (value: string) => value.replace(/[\\%_]/g, "\\$&");

const workflowKeyForType = (type?: string | null) => {
  if (type === "harvest") return "harvesting";
  if (type === "refurbish") return "refurbishment";
  return type || "harvesting";
};

/** FIX-304: serials compared case/space/punctuation-insensitive; < 4 chars = no serial. */
export const normalizeSerial = (value?: string | null) => {
  const n = normalize(value);
  return n.length >= 4 ? n : "";
};

const sameModel = (row: ApplianceMatch, ocr: Pick<OCRResult, "modelNumber" | "productNumber">) => {
  const model = normalize(ocr.modelNumber);
  const product = normalize(ocr.productNumber);
  const rowModel = normalize(row.model_number);
  const rowProduct = normalize(row.product_number);
  return (!!model && !!rowModel && model === rowModel) || (!!product && !!rowProduct && product === rowProduct);
};

const sameBrand = (row: ApplianceMatch, brand?: string | null) => {
  const b = normalize(brand);
  const rb = normalize(row.brand || row.name?.split(" ")[0]);
  return !!b && !!rb && b === rb;
};

export type ScanMatchDecision =
  | { kind: "open"; appliance: ApplianceMatch }
  | { kind: "confirm-no-serial"; appliance: ApplianceMatch; serial: string }
  | { kind: "choose"; candidates: ApplianceMatch[] }
  | { kind: "new" };

type ScanInput = Pick<OCRResult, "modelNumber" | "productNumber" | "serialNumber" | "brand">;

/**
 * FIX-304 (FD-27): the serial identifies the unit. A model/product match alone
 * never opens an appliance; it can only be offered for confirmation.
 */
export const decideScanMatch = (ocr: ScanInput, candidates: ApplianceMatch[]): ScanMatchDecision => {
  const serial = normalizeSerial(ocr.serialNumber);
  const modelMatches = candidates.filter((c) => sameModel(c, ocr));
  if (serial) {
    const bySerial = candidates.filter(
      (c) => normalizeSerial(c.serial_number) === serial && (sameModel(c, ocr) || sameBrand(c, ocr.brand)),
    );
    if (bySerial.length > 0) {
      const exact = bySerial.find((c) => normalize(c.model_number) === normalize(ocr.modelNumber));
      return { kind: "open", appliance: exact ?? bySerial[0] };
    }
    const noSerial = modelMatches.filter((c) => !normalizeSerial(c.serial_number));
    if (noSerial.length === 1) return { kind: "confirm-no-serial", appliance: noSerial[0], serial: ocr.serialNumber!.trim() };
    if (noSerial.length > 1) return { kind: "choose", candidates: noSerial };
    return { kind: "new" };
  }
  if (modelMatches.length > 0) return { kind: "choose", candidates: modelMatches };
  return { kind: "new" };
};

/** Active-company candidates (any creator) sharing model, product or serial; ordered newest first. */
export const collectApplianceMatches = async (ocrResult: ScanInput, companyId: string): Promise<ApplianceMatch[]> => {
  const searches = [ocrResult.modelNumber, ocrResult.productNumber, ocrResult.serialNumber]
    .map((value) => value?.trim())
    .filter((value): value is string => !!value);
  // FIX-304: with a serial, also fetch the brand's appliances so a stored serial in
  // another format ("45100004" vs "45 100-004") is still found by normalizeSerial.
  const brand = normalizeSerial(ocrResult.serialNumber) ? ocrResult.brand?.trim() : "";

  if (searches.length === 0 && !brand) return [];

  const select = "id, name, brand, model_number, serial_number, product_number, company_id";
  const results = await Promise.all([
    ...searches.map((value) =>
      supabase
        .from("appliances")
        .select(select)
        .eq("company_id", companyId)
        .or(
          `model_number.ilike.%${escapeLike(value)}%,product_number.ilike.%${escapeLike(value)}%,serial_number.ilike.%${escapeLike(value)}%`,
        )
        .limit(50),
    ),
    ...(brand
      ? [supabase.from("appliances").select(select).eq("company_id", companyId)
          .or(`brand.ilike.${escapeLike(brand)}`).not("serial_number", "is", null).limit(500)]
      : []),
  ]);

  const byId = new Map<string, ApplianceMatch>();
  results.forEach(({ data, error }) => {
    if (error) {
      console.error("Failed to resolve scanned appliance:", error);
      return;
    }
    (data || []).forEach((row) => byId.set(row.id, row as ApplianceMatch));
  });

  return onlyActiveCompany([...byId.values()], companyId);
};

/**
 * FIX-304 batch import: a row without a readable serial is skipped (never saved);
 * a row with a serial reuses only the appliance with that serial.
 */
export const batchImportRowMatch = (
  ocr: ScanInput,
  candidates: ApplianceMatch[],
): { skip: boolean; existingApplianceId?: string } => {
  if (!normalizeSerial(ocr.serialNumber)) return { skip: true };
  const decision = decideScanMatch(ocr, candidates);
  return { skip: false, existingApplianceId: decision.kind === "open" ? decision.appliance.id : undefined };
};

/** Saves a scanned serial on an appliance only if it still has none. Returns false if not saved. */
export const assignScannedSerial = async (applianceId: string, serial: string): Promise<boolean> => {
  const companyId = await fetchCurrentCompanyId();
  if (!companyId) return false;
  const { data, error } = await supabase
    .from("appliances")
    .update({ serial_number: serial.trim() })
    .eq("id", applianceId)
    .eq("company_id", companyId)
    .is("serial_number", null)
    .select("id");
  if (error) console.error("Failed to save scanned serial:", error);
  return !error && (data?.length ?? 0) > 0;
};

const findBestWorkOrder = async (applianceId: string, companyId: string): Promise<WorkOrderMatch | null> => {
  const { data, error } = await supabase
    .from("work_orders")
    .select("id, type, status, appliance_id, updated_at, created_at")
    .is("deleted_at", null)
    .eq("company_id", companyId)
    .eq("appliance_id", applianceId)
    .order("updated_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("Failed to resolve scanned work order:", error);
    return null;
  }

  const orders = ((data || []) as WorkOrderMatch[]).sort((a, b) => {
    const statusScore = (status: string | null) =>
      status === "in_progress" || status === "pending" ? 2 : status === "completed" ? 1 : 0;
    const statusDelta = statusScore(b.status) - statusScore(a.status);
    if (statusDelta !== 0) return statusDelta;
    return new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime();
  });

  return orders[0] || null;
};

export const destinationForAppliance = async (applianceId: string): Promise<ScanDestination | null> => {
  const companyId = await fetchCurrentCompanyId();
  if (!companyId) return null;
  const workOrder = await findBestWorkOrder(applianceId, companyId);
  if (workOrder) {
    const workflowKey = workflowKeyForType(workOrder.type);
    return {
      kind: "work-order",
      applianceId,
      workOrderId: workOrder.id,
      path: `/appliance/${applianceId}/workflow/${workflowKey}?wo=${workOrder.id}&workOrderId=${workOrder.id}`,
    };
  }
  return { kind: "appliance", applianceId, path: `/appliance/${applianceId}` };
};

export const resolveScanMatch = async (ocrResult: ScanInput): Promise<ScanMatchDecision> => {
  const companyId = await fetchCurrentCompanyId();
  if (!companyId) return { kind: "new" };
  return decideScanMatch(ocrResult, await collectApplianceMatches(ocrResult, companyId));
};

/** Opens only on a serial match (FIX-304); otherwise null. */
export const resolveScanDestination = async (ocrResult: ScanInput): Promise<ScanDestination | null> => {
  const decision = await resolveScanMatch(ocrResult);
  return decision.kind === "open" ? destinationForAppliance(decision.appliance.id) : null;
};
